import { z } from 'zod';
import type { ModuleKey } from './modules';
import { MENTIONS_MAX } from './notifications';

// ---------------------------------------------------------------------------------------------
// Colaboração: comentários, arquivos, fotos, links e evidências de execução (T-112, RF-41, RF-11; DEC-047).

/** Assuntos que recebem colaboração: objetivo, KPI, iniciativa, ação, reunião (T-121b) e item de diagnóstico (T-160a). */
export const COLLAB_SUBJECTS = ['objective', 'kpi', 'initiative', 'action', 'meeting', 'diagnosis'] as const;
export type CollabSubjectName = (typeof COLLAB_SUBJECTS)[number];

/** Tela de cada assunto (dentro de /empresas/:id) e o módulo que ela exige: onde o assunto abre pelo link. */
export const COLLAB_SUBJECT_PAGES: Record<CollabSubjectName, { path: string; module: ModuleKey }> = {
  objective: { path: 'estrategia/objetivos', module: 'objetivos' },
  kpi: { path: 'indicadores/historico', module: 'metas-resultados' },
  initiative: { path: 'execucao/iniciativas', module: 'iniciativas' },
  action: { path: 'execucao/planos-acao', module: 'planos-acao' },
  meeting: { path: 'gestao/reunioes', module: 'reunioes-decisoes' },
  diagnosis: { path: 'diagnostico', module: 'diagnostico' },
};

/** Caminho da tela do assunto, com o projeto e o assunto aberto (links das notificações e da busca global). */
export function collabSubjectPath(p: {
  organizationId: string;
  projectId: string;
  type: CollabSubjectName;
  id: string;
  /** Ação: o plano dela. */
  planId?: string | null;
}): string {
  const query = new URLSearchParams({ projeto: p.projectId });
  switch (p.type) {
    case 'objective':
      query.set('objetivo', p.id);
      break;
    case 'kpi':
      query.set('kpi', p.id);
      break;
    case 'initiative':
      query.set('iniciativa', p.id);
      break;
    case 'action':
      if (p.planId) query.set('plano', p.planId);
      query.set('acao', p.id);
      break;
    case 'meeting':
      query.set('reuniao', p.id);
      break;
    case 'diagnosis':
      query.set('item', p.id);
      break;
  }
  return `/empresas/${p.organizationId}/${COLLAB_SUBJECT_PAGES[p.type].path}?${query.toString()}`;
}

export const COLLAB_KINDS = ['COMMENT', 'FILE', 'LINK'] as const;
export type CollabKindName = (typeof COLLAB_KINDS)[number];

export const COLLAB_COMMENT_MAX = 4000;
export const COLLAB_LINK_TITLE_MAX = 200;

export const collabItemSchema = z.object({
  id: z.string().uuid(),
  kind: z.enum(COLLAB_KINDS),
  /** Comentário ou título do link. */
  body: z.string().nullable(),
  url: z.string().nullable(),
  file: z
    .object({
      name: z.string(),
      size: z.number().int(),
      mimeType: z.string(),
      /** Foto (imagem) ou documento. */
      isImage: z.boolean(),
      /** Pré-visualização na tela (imagem ou PDF), por URL assinada que expira. */
      previewUrl: z.string().nullable(),
      downloadUrl: z.string(),
    })
    .nullable(),
  isEvidence: z.boolean(),
  /** Pessoas mencionadas no comentário (T-113; DEC-048): destacadas na tela. */
  mentions: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  author: z.object({ id: z.string().uuid().nullable(), name: z.string() }),
  createdAt: z.string(),
  /** Quem pede pode excluir (e mudar a marcação de evidência): o autor ou quem tem "excluir" no módulo. */
  canDelete: z.boolean(),
});
export type CollabItem = z.infer<typeof collabItemSchema>;
export const collabListSchema = z.object({
  items: z.array(collabItemSchema),
  /** Quem pede pode colaborar (o assunto e o projeto aceitam itens novos). */
  canPost: z.boolean(),
  /** Validade das URLs assinadas (segundos): a tela recarrega a lista antes de vencerem. */
  urlTtlSeconds: z.number().int(),
});
export type CollabList = z.infer<typeof collabListSchema>;

const evidenceFlag = z.boolean().default(false);

export const collabCommentInputSchema = z.object({
  body: z
    .string()
    .trim()
    .min(1, 'Escreva o comentário.')
    .max(COLLAB_COMMENT_MAX, `O comentário pode ter no máximo ${COLLAB_COMMENT_MAX} caracteres.`),
  isEvidence: evidenceFlag,
  /** Pessoas mencionadas com @ (DEC-048). */
  mentionIds: z
    .array(z.string().uuid('Menção inválida.'))
    .max(MENTIONS_MAX, `Mencione no máximo ${MENTIONS_MAX} pessoas por comentário.`)
    .default([])
    .transform((ids) => [...new Set(ids)]),
});
export type CollabCommentInput = z.input<typeof collabCommentInputSchema>;

export const collabLinkInputSchema = z.object({
  url: z
    .string()
    .trim()
    .max(2000, 'Link longo demais.')
    .refine((v) => {
      try {
        const u = new URL(v);
        return u.protocol === 'http:' || u.protocol === 'https:';
      } catch {
        return false;
      }
    }, 'Informe um link que comece com http:// ou https://.'),
  title: z
    .string()
    .trim()
    .max(COLLAB_LINK_TITLE_MAX, `O título pode ter no máximo ${COLLAB_LINK_TITLE_MAX} caracteres.`)
    .nullable()
    .optional()
    .transform((v) => v || null),
  isEvidence: evidenceFlag,
});
export type CollabLinkInput = z.input<typeof collabLinkInputSchema>;

export const collabEvidenceInputSchema = z.object({ isEvidence: z.boolean() });
